import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider
} from '@mui/material';
import { Add, Edit, Delete, VideoLibrary, Upload, CloudUpload } from '@mui/icons-material';
import { createTemplate, deleteTemplateById, getAllTemplates, updateTemplateById } from '../../apis/apiList/templateApi';
import BeatLoader from "react-spinners/BeatLoader";
import { useApp } from '../../context/AppContext';
import { useMessage } from '../../context/MessageContext';
import theme from '../../theme/theme';


const Admin = () => {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    // thumbnail: '',
    // category: ''
  });
  const [selectedVideoFile, setSelectedVideoFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  const [openVideoDialog, setOpenVideoDialog] = useState(false);
  const [selectedVideoSrc, setSelectedVideoSrc] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const ITEMS_PER_PAGE = 8;
  const [page, setPage] = useState(1);

  const { showMessage } = useMessage();
  const { userDetails } = useApp();

  const categories = ['Intro', 'Business', 'Creative', 'Minimal', 'Technology', 'Marketing'];

  useEffect(() => {
    
    async function fetchAllTemplates() {
      try {
        const templateList = await getAllTemplates({
          headers: {
            'Content-Type': 'multipart/form-data',
            "x-access-token": userDetails?.jwtToken || ''
          }
        })
        console.log('templateList', templateList);
        setTemplates(templateList.templates);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching templates:', error);
        showMessage('Failed to load templates', 'error');
        setIsLoading(false);
      }
    }
    
    fetchAllTemplates();
  }, []);

  const handleAddTemplate = () => {
    setEditingTemplate(null);
    setFormData({
      title: '',
      description: '',
    });
    setSelectedVideoFile(null);
    setDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      title: template.title,
      description: template.description,
      // thumbnail: template.thumbnail,
      // category: template.category
    });
    setSelectedVideoFile(null);
    setDialogOpen(true);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (templateToDelete) {
      setIsDeleting(true);
      try {
        const response = await deleteTemplateById(templateToDelete._id, {
          headers: {
            "x-access-token": userDetails?.jwtToken || ''
          }
        });
        console.log('Delete response:', response);
        const updatedTemplates = templates.filter(t => t._id !== response.template._id);
        console.log('Template deleted:', templateToDelete);
        const totalPages = Math.ceil(updatedTemplates.length / ITEMS_PER_PAGE);
        const newPage = page > totalPages ? totalPages : page;

        setTemplates(updatedTemplates);
        setPage(newPage);
        setIsDeleting(false);
      } catch (error) {
        console.error('Error deleting template:', error);
        showMessage('Failed to delete template', 'error');
        setIsDeleting(false);
        return;
      }
      
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  const handleVideoFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedVideoFile(file);
      console.log('Selected video file:', file.name);
    }
  };

  const handleSaveTemplate = async () => {
    setIsSaving(true);
    try {
      if (editingTemplate) {

        let templateformData = new FormData();

        if (selectedVideoFile) {
          templateformData.append('videoFile', selectedVideoFile);
        }
        templateformData.append('title', formData.title);
        templateformData.append('description', formData.description);

        const templateRes = await updateTemplateById(editingTemplate._id, templateformData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            "x-access-token": userDetails?.jwtToken || ''
          }
        });

        console.log('Template updated:', templateRes);
        
        let newTemplateDetails = templateRes.template;

        setTemplates(templates.map(t => 
          t._id === editingTemplate._id
            ? newTemplateDetails
            : t
        ));

        showMessage(templateRes.message, 'success');
        
      } else {
        // Add new template
        let templateformData = new FormData();

        if (selectedVideoFile) {
          templateformData.append('videoFile', selectedVideoFile);
        }
        templateformData.append('title', formData.title);
        templateformData.append('description', formData.description);

        const templateRes = await createTemplate(templateformData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            "x-access-token": userDetails?.jwtToken || ''
          }
        });

        console.log('Template created:', templateRes);
        
        let newTemplateDetails = templateRes.template;
        const newTemplate = {
          _id: newTemplateDetails._id,
          title: newTemplateDetails?.title,
          description: newTemplateDetails?.description,
          videoFile: newTemplateDetails?.videoId,
          videoId: newTemplateDetails?.videoId,
          createdAt: newTemplateDetails?.createdAt
        };
        setTemplates([newTemplate, ...templates]);
        console.log('Template added:', newTemplate._id);

        showMessage(templateRes.message, 'success');
      }
      setDialogOpen(false);
      setSelectedVideoFile(null);  
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if(isLoading) {
    return (
      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <BeatLoader
            color={theme.palette.primary.main}
            loading={isLoading}
            size={25}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Template Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddTemplate}
          sx={{ px: 3 }}
        >
          Add Template
        </Button>
      </Box>

      {
        templates && templates.length > 0 &&
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#b5b5b5' }}>
                  <TableCell>Template</TableCell>
                  <TableCell>Title</TableCell>
                  {/* <TableCell>Category</TableCell> */}
                  <TableCell>Description</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map((template) => (
                  <TableRow key={template._id} hover>
                    <TableCell>
                      {/* <Avatar
                        src={template.thumbnail}
                        variant="rounded"
                        sx={{ width: 60, height: 40 }}
                      >
                        <VideoLibrary />
                      </Avatar> */}
                      {/* <video controls width={100} height={100}>
                        <source src={template.videoId} >
                        </source>
                      </video> */}
                      <Box 
                        component="div"
                        sx={{ 
                          position: 'relative', 
                          width: 100, 
                          height: 60, 
                          borderRadius: 1, 
                          overflow: 'hidden', 
                          cursor: 'pointer', 
                          boxShadow: 1 
                        }}
                        onClick={() => {
                          setSelectedVideoSrc(template.videoId);
                          setOpenVideoDialog(true);
                        }}
                      >
                        {/* <video 
                          src={template.videoId}
                          muted
                          playsInline
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        /> */}
                        <iframe
                          src={`https://player.vimeo.com/video/${template.videoId}`}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            bgcolor: 'rgba(0,0,0,0.3)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <VideoLibrary sx={{ color: 'white' }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'semibold' }}>
                        {template.title}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Chip 
                        label={template.category} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell> */}
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {template.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(template.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        sx={{ mr: 1 }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(template)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box display="flex" justifyContent="center" m={'15px 0px'}>
            <Pagination
              count={Math.max(1, Math.ceil(templates.length / ITEMS_PER_PAGE))}
              page={page}
              onChange={(e, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </Paper>
      }

      {/* <Dialog open={openVideoDialog} onClose={() => setOpenVideoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle >Template Video</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', height: 'auto', minHeight: '215px', maxHeight: '900px' }}>
          <video controls autoPlay style={{ width: '100%', borderRadius: 8 }}>
            <source src={selectedVideoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog> */}
      <Dialog open={openVideoDialog} onClose={() => setOpenVideoDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Video</DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'auto',
            minHeight: '215px',
            maxHeight: '90vh',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              paddingTop: '56.25%', // 16:9 aspect ratio
            }}
          >
            <iframe
              src={`https://player.vimeo.com/video/${selectedVideoSrc}`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                borderRadius: '8px',
              }}
            ></iframe>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {templates.length === 0 && (
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 2,
            mt: 3
          }}
        >
          <VideoLibrary sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Add your first template to get started
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleAddTemplate}>
            Add Template
          </Button>
        </Box>
      )}

      {/* Add/Edit Template Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 16 }}>
          {editingTemplate ? 'Edit Template' : 'Add New Template'}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Template title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              className={"topic-text"}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />
            {/* <TextField
              label="Category"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              select
              fullWidth
              required
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Thumbnail URL"
              value={formData.thumbnail}
              onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
              fullWidth
              required
            /> */}
            
            {/* Video File Upload */}
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: isDragging ? '#f0f8ff' : 'transparent',
                transition: 'background-color 0.2s',
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('video/')) {
                  setSelectedVideoFile(file);
                }
              }}
            >
              <input
                accept="video/*"
                style={{ display: 'none' }}
                id="video-upload"
                type="file"
                onChange={handleVideoFileChange}
              />
              <label htmlFor="video-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ mb: 2 }}
                >
                  Upload Video File
                </Button>
              </label>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Drag & drop a video file here, or click to browse
              </Typography>

              {selectedVideoFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Selected: {selectedVideoFile.name}
                </Typography>
              )}
              {!selectedVideoFile && editingTemplate?.videoUrl && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Current video: {editingTemplate.name}.mp4
                </Typography>
              )}
              {!selectedVideoFile && !editingTemplate && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No video file selected
                </Typography>
              )}
            </Box>
            {/* <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center' }}>
              <input
                accept="video/*"
                style={{ display: 'none' }}
                id="video-upload"
                type="file"
                onChange={handleVideoFileChange}
              />
              <label htmlFor="video-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ mb: 2 }}
                >
                  Upload Video File
                </Button>
              </label>
              {selectedVideoFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedVideoFile.name}
                </Typography>
              )}
              {!selectedVideoFile && editingTemplate?.videoUrl && (
                <Typography variant="body2" color="text.secondary">
                  Current video: {editingTemplate.name}.mp4
                </Typography>
              )}
              {!selectedVideoFile && !editingTemplate && (
                <Typography variant="body2" color="text.secondary">
                  No video file selected
                </Typography>
              )}
            </Box> */}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '20px 24px', paddingTop: 0 }}>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained"
            disabled={ !formData.title || !formData.description || (!selectedVideoFile && !editingTemplate) || isSaving }
          >
            {isSaving ? (editingTemplate ? 'Updating...' : 'Adding...') : (editingTemplate ? 'Update Template' : 'Add Template') }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>
          Delete Template
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to delete <strong>{templateToDelete?.title}</strong> template? This action cannot be undone. <br />
              All projects using this template will be affected.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '20px 24px', paddingTop: 0 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="contained">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            disabled={ isDeleting }
          >
            {isDeleting ? 'Deleting...' : 'Delete Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admin;